import * as React from "react";
import { Form, Dropdown, DropdownProps } from "semantic-ui-react";
import { isEqual, memoize } from "lodash";
import { validate } from "not-valid";

import { QuestionProps, AnsweredQuestion, DropdownOption } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface DropdownQuestionAdditionalProps {
    filterOldOptions?: boolean;
    options: DropdownOption[] | undefined;
    search?: boolean;
    upward?: boolean;
    multiple?: boolean;
    sortAlphabetically?: boolean;
    clearable?: boolean;
    selectOnBlur?: boolean;
}

type DropdownQuestionProps = QuestionProps & DropdownQuestionAdditionalProps;

export const sortDropdownOptionsAlphabetically = memoize(options => options.sort((a: DropdownOption, b: DropdownOption) => (a.text > b.text) ? 1 : -1));

class DropdownQuestion extends React.Component<DropdownQuestionProps> {
    constructor(props: DropdownQuestionProps) {
        super(props);

        this.onChange = this.onChange.bind(this);
    }

    public render() {
        if (this.props.hidden || isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        return (
            <QuestionWrapper
                popupContent={this.props.popupContent}
                inline={this.props.inline}
                question={this.props.questionConfig.questionText}
                mandatory={this.props.mandatory}
                hideDivider={this.props.hideDivider}
                questionClassName={this.props.questionClassName}
                error={this.props.showError && this.props.mandatory && !this.props.answer && "This field must be completed"}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
            >
                <Form.Field>
                    {this.renderValue()}
                </Form.Field>
            </QuestionWrapper>
        );
    }

    public componentDidMount() {
        this.validateAndUpdateQuestion(this.props.answer as string);
    }

    public componentDidUpdate(oldProps: QuestionProps) {
        if (!isEqual(oldProps.validationDependencies, this.props.validationDependencies)) {
            this.validateAndUpdateQuestion(this.props.answer as string);
        }

        if (!isEqual(oldProps.answer, this.props.answer)) {
            this.validateAndUpdateQuestion(this.props.answer as string);
        }
    }

    private getOptions = () => {
        if (Array.isArray(this.props.options)) {
            return this.props.options;
        }

        return [];
    };

    private renderValue() {
        if (this.props.readonly) {
            if (this.props.multiple) {
                return (
                    <ul className={"question-ul"}>
                        {this.getOptions()
                            .filter(o => (this.props.answer as string[] || []).indexOf(o.value) !== -1)
                            .map(o => <li key={o.value}>{o.text}</li>)
                        }
                    </ul>);
            } else {
                return <div>{this.getOptions().find(o => o.value === this.props.answer)?.text ?? this.props.answer}</div>;
            }
        }

        let options = this.getOptions();
        if (this.props.filterOldOptions) {
            options = options.filter(o => !o.old);
        }
        if (this.props.sortAlphabetically) {
            options = sortDropdownOptionsAlphabetically(options);
        }

        return (
            <Dropdown
                multiple={this.props.multiple}
                value={this.props.answer as string}
                options={options}
                className="selection"
                onChange={this.onChange}
                search={this.props.search}
                upward={this.props.upward}
                clearable={!this.props.mandatory || this.props.clearable}
                selectOnBlur={this.props.selectOnBlur}
            />
        );
    }

    private onChange(event: React.MouseEvent<HTMLInputElement>, data: DropdownProps) {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const newAnswer = data.value as string | string[];
        const userFriendlyAnswer = this.getOptions()?.find(o => o.value === newAnswer)?.text;
        const newQuestion: AnsweredQuestion = {
            question: this.props.questionConfig.questionText,
            answer: newAnswer,
            isValid: !!data.value || !this.props.mandatory,
            userFriendlyAnswer
        };
        if (this.props.updateQuestion) {
            this.props.updateQuestion(newQuestion);
        }
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }
    }

    private validateAndUpdateQuestion(answer: string) {
        if (this.props.readonly || isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const userFriendlyAnswer = this.getOptions()?.find(o => o.value === this.props.answer)?.text;
        const newQuestion: AnsweredQuestion = {
            question: this.props.questionConfig.questionText,
            answer,
            userFriendlyAnswer
        };
        if (!(this.props.validators?.length)) {
            newQuestion.isValid = !this.props.mandatory || !!answer;
        }
        if (this.props.updateQuestion) {
            this.props.updateQuestion(newQuestion);
        }
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }
        if (this.props.validators?.length) {
            this.props.validateQuestion(this.getValidatedQuestion(this.props.questionConfig.questionText, answer));
        }
    }

    private async getValidatedQuestion(question: string, answer: string): Promise<AnsweredQuestion> {
        const errors = await validate(this.props.validators || [], answer);
        this.setState({ error: errors[0] });
        const isValid = errors.length === 0 && (!!answer || !this.props.mandatory);
        const userFriendlyAnswer = (this.props.options ?? []).find(o => o.value === this.props.answer)?.text;
        return { question, answer, isValid, userFriendlyAnswer };
    }
}

export { DropdownQuestion };
